(function () {
  const DEBUG = true;
  const log = (...args) => { if (DEBUG) console.log('[Subscription Debug]', ...args); };

  const SUBSCRIPTION_STORAGE_KEY = 'subscription_cart_state_v2';
  let subscriptionBusy = false;
  let subscriptionPopupOpen = false;
  let validationDebounceTimer = null;

  let recentRunTimestamps = [];
  function circuitBreakerTripped() {
    const now = Date.now();
    recentRunTimestamps = recentRunTimestamps.filter((t) => now - t < 3000);
    recentRunTimestamps.push(now);
    if (recentRunTimestamps.length > 8) {
      console.error('[Subscription] Circuit breaker tripped — halting to prevent cart damage.');
      return true;
    }
    return false;
  }

  function getStoredState() {
    try {
      return JSON.parse(localStorage.getItem(SUBSCRIPTION_STORAGE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function saveGroupVariantState(productId, sellingPlanId, groupItems) {
    const state = getStoredState();
    const variantMap = {};
    groupItems.forEach((item) => {
      variantMap[item.variant_id] = (variantMap[item.variant_id] || 0) + item.quantity;
    });
    state[`${productId}_${sellingPlanId}`] = { productId, sellingPlanId, variantMap };
    localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(state));
    log('Saved variant state', { productId, sellingPlanId, variantMap });
  }

  function getStoredGroupVariantMap(productId, sellingPlanId) {
    const state = getStoredState();
    return state[`${productId}_${sellingPlanId}`]?.variantMap || {};
  }

  function getCartItems() {
    const raw = window.Rebuy?.SmartCart?.cart?.items || window.Rebuy?.SmartCart?.items?.() || [];
    const byKey = new Map();
    raw.forEach((item) => {
      if (byKey.has(item.key)) {
        byKey.get(item.key).quantity += item.quantity;
      } else {
        byKey.set(item.key, { ...item });
      }
    });
    return [...byKey.values()];
  }

  function getRequiredSubscriptionQuantity(item) {
    const planName = item?.selling_plan_allocation?.selling_plan?.name || '';
    const cleanedName = planName.replace(/^Subscribe\s*&\s*Save/i, '').replace(/\d+%/g, '').trim();
    const quantityMatch = cleanedName.match(/(\d+)\s*(Tub|Tubs|Box|Boxes|Bottle|Bottles|Pack|Packs|Jar|Jars|Bag|Bags)\b/i);
    return quantityMatch ? parseInt(quantityMatch[1], 10) : 1;
  }

  const MULTI_FLAVOR_SUBSCRIPTION_HANDLES = ['mother-of-all-greens'];
  function allowsIndependentSubscriptions(item) {
    const handle = (item?.handle || item?.url || '').toString().toLowerCase();
    const title = (item?.product_title || '').toString().toLowerCase();
    return MULTI_FLAVOR_SUBSCRIPTION_HANDLES.some((needle) => handle.includes(needle) || title.includes(needle.replace(/-/g, ' ')));
  }

  function getVariantQuantityMap(items) {
    const map = {};
    items.forEach((item) => { map[item.variant_id] = (map[item.variant_id] || 0) + item.quantity; });
    return map;
  }

  function isProportionalDuplicateAdd(previousVariantMap, currentVariantMap) {
    const previousIds = Object.keys(previousVariantMap);
    const currentIds = Object.keys(currentVariantMap);

    if (previousIds.length === 0 || previousIds.length !== currentIds.length) return false;
    if (!previousIds.every((id) => currentVariantMap[id] !== undefined)) return false;

    const deltas = {};
    for (const id of previousIds) {
      const delta = currentVariantMap[id] - previousVariantMap[id];
      if (delta < 0) return false;
      deltas[id] = delta;
    }

    const refId = previousIds.find((id) => deltas[id] > 0);
    if (!refId) return false;

    const refDelta = deltas[refId];
    const refPrevious = previousVariantMap[refId];
    return previousIds.every((id) => deltas[id] * refPrevious === refDelta * previousVariantMap[id]);
  }

  function waitForCartChangeConfirmation(timeoutMs = 4000) {
    return new Promise((resolve) => {
      let done = false;
      const handler = () => {
        if (done) return;
        done = true;
        document.removeEventListener('rebuy:cart.change', handler);
        clearTimeout(timer);
        resolve(true);
      };
      const timer = setTimeout(() => {
        if (done) return;
        done = true;
        document.removeEventListener('rebuy:cart.change', handler);
        resolve(false);
      }, timeoutMs);
      document.addEventListener('rebuy:cart.change', handler);
    });
  }

  async function applyVariantTargets(productId, sellingPlanId, targetVariantQtyMap, label) {
    log('applyVariantTargets START', label, targetVariantQtyMap);

    for (const variantId of Object.keys(targetVariantQtyMap)) {
      const target = targetVariantQtyMap[variantId];
      let guard = 0;

      while (guard < 25) {
        guard++;
        const liveVariantItems = getCartItems().filter((i) => {
          return i.product_id === productId &&
            i.selling_plan_allocation?.selling_plan?.id === sellingPlanId &&
            String(i.variant_id) === String(variantId);
        });

        if (!liveVariantItems.length) break;

        let remaining = target;
        let wroteSomething = false;

        for (const item of liveVariantItems) {
          const desired = Math.min(item.quantity, Math.max(remaining, 0));
          remaining -= desired;

          if (desired !== item.quantity) {
            log('applyVariantTargets: setting', item.key, 'variant', variantId, 'from', item.quantity, 'to', desired);
            try {
              await window.Rebuy.Cart.setItemQuantity(item, desired);
            } catch (err) {
              console.error('[Subscription] Failed to update item', item?.key, err);
            }
            await waitForCartChangeConfirmation();
            wroteSomething = true;
            break;
          }
        }

        if (!wroteSomething) break;
      }
    }

    log('applyVariantTargets DONE', label);
  }

  function openSubscriptionPopup({
    title = '', message = '', confirmText = 'OK', cancelText = 'Cancel',
    hideCancel = false, onConfirm = null, onCancel = null,
  }) {
    const existingPopup = document.querySelector('.subscription-cart-popup');
    if (existingPopup) existingPopup.remove();
    subscriptionPopupOpen = true;
    log('Popup opened:', title);

    const popup = document.createElement('div');
    popup.className = 'subscription-cart-popup';
    popup.innerHTML = `
      <div class="subscription-cart-popup__overlay"></div>
      <div class="subscription-cart-popup__container">
        <div class="subscription-cart-popup__content">
          <div class="subscription-cart-popup__loader">
            <svg height="40" width="40" class="svg-loader">
              <circle r="16" cx="20" cy="20"></circle>
              <circle stroke-dasharray="87.96459430051421 87.96459430051421" r="16" cx="20" cy="20"></circle>
            </svg>
          </div>
          <div class="subscription-cart-popup__inner">
            <h3>${title}</h3>
            <p>${message.replace(/\n/g, '<br>')}</p>
            <div class="subscription-cart-popup__actions">
              ${!hideCancel ? `<button class="subscription-popup-cancel"><span class="text-link">${cancelText}</span></button>` : ''}
              <span id="button" class="btn btn--solid btn--primary">
                <button class="subscription-popup-confirm"><span>${confirmText}</span></button>
              </span>
            </div>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(popup);
    document.dispatchEvent(new CustomEvent('theme:scroll:lock'));

    popup.querySelector('.subscription-popup-confirm')?.addEventListener('click', async () => {
      popup.classList.add('is-loading');
      if (onConfirm) await onConfirm();
      popup.remove();
      subscriptionPopupOpen = false;
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock'));
    });
    popup.querySelector('.subscription-popup-cancel')?.addEventListener('click', async () => {
      popup.classList.add('is-loading');
      if (onCancel) await onCancel();
      popup.remove();
      subscriptionPopupOpen = false;
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock'));
    });
  }

  function groupSubscriptionItems(cartItems) {
    const groups = {};
    cartItems.forEach((item) => {
      if (!item.selling_plan_allocation) return;
      const sellingPlan = item.selling_plan_allocation?.selling_plan;
      const sellingPlanId = sellingPlan?.id || 'none';
      const sellingPlanName = sellingPlan?.name || 'Subscription';
      const groupKey = `${item.product_id}_${sellingPlanId}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          productId: item.product_id, sellingPlanId,
          requiredQty: getRequiredSubscriptionQuantity(item),
          currentQty: 0, items: [], sellingPlanName,
        };
      }
      groups[groupKey].currentQty += item.quantity;
      groups[groupKey].items.push(item);
    });
    return groups;
  }

  async function validateSubscriptionCart() {
    if (subscriptionBusy || subscriptionPopupOpen) {
      log('Skipping — busy or popup open');
      return;
    }
    if (circuitBreakerTripped()) return;

    const cartItems = getCartItems();
    log('Validating. Live items:', cartItems.map((i) => ({ key: i.key, variant_id: i.variant_id, qty: i.quantity })));
    if (!cartItems.length) return;

    const groups = groupSubscriptionItems(cartItems);

    for (const [groupKey, group] of Object.entries(groups)) {
      try {
        const { productId, sellingPlanId, requiredQty, currentQty, items, sellingPlanName } = group;
        log(`Group ${groupKey}: currentQty=${currentQty}, requiredQty=${requiredQty}`);

        const currentVariantMap = getVariantQuantityMap(items);

        if (requiredQty === 1) {
          const duplicateVariantIds = Object.entries(currentVariantMap)
            .filter(([, qty]) => qty > 1)
            .map(([variantId]) => variantId);

          if (duplicateVariantIds.length) {
            const targetMap = {};
            duplicateVariantIds.forEach((variantId) => { targetMap[variantId] = 1; });
            openSubscriptionPopup({
              title: 'Subscription Already In Cart',
              message: `"${sellingPlanName}" only allows one quantity of the same subscription product in cart.`,
              confirmText: 'OK', hideCancel: true,
              onConfirm: async () => {
                subscriptionBusy = true;
                await applyVariantTargets(productId, sellingPlanId, targetMap, 'requiredQty=1 duplicate');
                saveGroupVariantState(productId, sellingPlanId, getCartItems().filter((i) => i.product_id === productId && i.selling_plan_allocation?.selling_plan?.id === sellingPlanId));
                subscriptionBusy = false;
              },
            });
            return;
          }

          const variantIds = Object.keys(currentVariantMap);
          if (variantIds.length > 1 && items.every(allowsIndependentSubscriptions)) continue;

          if (variantIds.length > 1) {
            const storedVariantMap = getStoredGroupVariantMap(productId, sellingPlanId);
            let oldVariantId = variantIds.find((id) => storedVariantMap[id] !== undefined) || variantIds[0];
            let newVariantId = variantIds.find((id) => id !== oldVariantId) || variantIds[variantIds.length - 1];
            const oldItem = items.find((i) => String(i.variant_id) === String(oldVariantId));
            const newItem = items.find((i) => String(i.variant_id) === String(newVariantId));

            openSubscriptionPopup({
              title: 'Only One Subscription Allowed',
              message: `For "${sellingPlanName}" only one subscription option is allowed.\n\nPlease select which option you want to keep.`,
              confirmText: `Keep ${oldItem.variant_title}`, cancelText: `Keep ${newItem.variant_title}`,
              onConfirm: async () => {
                subscriptionBusy = true;
                await applyVariantTargets(productId, sellingPlanId, { [newVariantId]: 0 }, 'keep old variant');
                saveGroupVariantState(productId, sellingPlanId, getCartItems().filter((i) => i.product_id === productId && i.selling_plan_allocation?.selling_plan?.id === sellingPlanId));
                subscriptionBusy = false;
              },
              onCancel: async () => {
                subscriptionBusy = true;
                await applyVariantTargets(productId, sellingPlanId, { [oldVariantId]: 0 }, 'keep new variant');
                saveGroupVariantState(productId, sellingPlanId, getCartItems().filter((i) => i.product_id === productId && i.selling_plan_allocation?.selling_plan?.id === sellingPlanId));
                subscriptionBusy = false;
              },
            });
            return;
          }
          continue;
        }

        if (currentQty === requiredQty) {
          saveGroupVariantState(productId, sellingPlanId, items);
          continue;
        }

        const previousVariantMap = getStoredGroupVariantMap(productId, sellingPlanId);
        log(`Group ${groupKey}: stored variant map`, previousVariantMap);

        if (currentQty > requiredQty) {
          const previousVariantIds = Object.keys(previousVariantMap);
          const currentVariantIds = Object.keys(currentVariantMap);
          const sameVariantSet = previousVariantIds.length > 0 &&
            previousVariantIds.length === currentVariantIds.length &&
            previousVariantIds.every((id) => currentVariantMap[id] !== undefined);

          const isPureDuplicate = sameVariantSet && isProportionalDuplicateAdd(previousVariantMap, currentVariantMap);

          log(`Group ${groupKey}: sameVariantSet=${sameVariantSet}, isPureDuplicate=${isPureDuplicate}`);

          if (isPureDuplicate) {
            const targetMap = {};
            previousVariantIds.forEach((id) => { targetMap[id] = previousVariantMap[id]; });

            openSubscriptionPopup({
              title: 'Subscription Already In Cart',
              message: `"${sellingPlanName}" already exists in cart with this configuration.\n\nOnly ${requiredQty} total products are allowed.`,
              confirmText: 'OK', hideCancel: true,
              onConfirm: async () => {
                subscriptionBusy = true;
                await applyVariantTargets(productId, sellingPlanId, targetMap, 'trim duplicate');
                saveGroupVariantState(productId, sellingPlanId, getCartItems().filter((i) => i.product_id === productId && i.selling_plan_allocation?.selling_plan?.id === sellingPlanId));
                subscriptionBusy = false;
              },
            });
            return;
          }

          if (previousVariantIds.length > 0) {
            const deltaMap = {};
            currentVariantIds.forEach((id) => {
              const previous = previousVariantMap[id] || 0;
              const current = currentVariantMap[id];
              deltaMap[id] = Math.max(0, current - previous);
            });

            let remainingNew = requiredQty;
            const trimmedNewTargetMap = {};
            currentVariantIds.forEach((id) => {
              const keep = Math.min(deltaMap[id], Math.max(remainingNew, 0));
              trimmedNewTargetMap[id] = keep;
              remainingNew -= keep;
            });

            const revertTargetMap = {};
            currentVariantIds.forEach((id) => { revertTargetMap[id] = 0; });
            previousVariantIds.forEach((id) => { revertTargetMap[id] = previousVariantMap[id]; });

            openSubscriptionPopup({
              title: 'Replace Subscription Products?',
              message: `A different "${sellingPlanName}" configuration was added.\n\nPlease select which products you want to keep.`,
              confirmText: 'Keep New Products', cancelText: 'Keep Existing Products',
              onConfirm: async () => {
                subscriptionBusy = true;
                await applyVariantTargets(productId, sellingPlanId, trimmedNewTargetMap, 'keep new config (by delta)');
                saveGroupVariantState(productId, sellingPlanId, getCartItems().filter((i) => i.product_id === productId && i.selling_plan_allocation?.selling_plan?.id === sellingPlanId));
                subscriptionBusy = false;
              },
              onCancel: async () => {
                subscriptionBusy = true;
                await applyVariantTargets(productId, sellingPlanId, revertTargetMap, 'keep old config (reverted)');
                saveGroupVariantState(productId, sellingPlanId, getCartItems().filter((i) => i.product_id === productId && i.selling_plan_allocation?.selling_plan?.id === sellingPlanId));
                subscriptionBusy = false;
              },
            });
            return;
          }

          let remaining = requiredQty;
          const targetMap = {};
          Object.keys(currentVariantMap).forEach((variantId) => {
            const keep = Math.min(currentVariantMap[variantId], Math.max(remaining, 0));
            targetMap[variantId] = keep;
            remaining -= keep;
          });

          openSubscriptionPopup({
            title: 'Subscription Quantity Adjusted',
            message: `"${sellingPlanName}" allows a maximum of ${requiredQty} products.\n\nYour cart has been adjusted to match.`,
            confirmText: 'OK', hideCancel: true,
            onConfirm: async () => {
              subscriptionBusy = true;
              await applyVariantTargets(productId, sellingPlanId, targetMap, 'FIFO trim');
              saveGroupVariantState(productId, sellingPlanId, getCartItems().filter((i) => i.product_id === productId && i.selling_plan_allocation?.selling_plan?.id === sellingPlanId));
              subscriptionBusy = false;
            },
          });
          return;
        }

        if (currentQty < requiredQty) {
          openSubscriptionPopup({
            title: 'Subscription Quantity Issue Detected',
            message: `"${sellingPlanName}" requires ${requiredQty} products.\n\nCurrently only ${currentQty} products are in cart. These will be removed from your cart.`,
            confirmText: 'OK', hideCancel: true,
            onConfirm: async () => {
              subscriptionBusy = true;
              const targetMap = {};
              Object.keys(currentVariantMap).forEach((id) => { targetMap[id] = 0; });
              await applyVariantTargets(productId, sellingPlanId, targetMap, 'remove under-quantity group');
              subscriptionBusy = false;
            },
          });
          return;
        }
      } catch (err) {
        console.error('[Subscription Validation Error - group]', groupKey, err);
      }
    }
  }

  function runValidation() {
    clearTimeout(validationDebounceTimer);
    validationDebounceTimer = setTimeout(() => {
      validateSubscriptionCart().catch((error) => console.error('[Subscription Validation Error]', error));
    }, 300);
  }

  document.addEventListener('rebuy:cart.ready', () => runValidation());
  document.addEventListener('rebuy:smartcart.ready', () => runValidation());
  document.addEventListener('rebuy:cart.add', () => runValidation());
  document.addEventListener('rebuy:cart.change', () => runValidation());

  (function pollForInitialReady() {
    const interval = setInterval(() => {
      const items = getCartItems();
      if (items.length || window.Rebuy?.SmartCart?.status === 'ready') {
        clearInterval(interval);
        runValidation();
      }
    }, 300);
  })();
})();